import { Box, Tab, TabList, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import {
  ColorIcon,
  colors,
  customEvent,
  HStack,
  NextLink,
  VStack,
} from "goi_common";
import useIntersectionObserver from "goi_common/src/hooks/useIntersectionObserver";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import CareRegionCard from "./CareRegionCard";

const TAB_NAME = ["list", "check"];
const BATCH_SIZE = 30;

export default function CareRegionTabSection({
  regionCares,
  sidoName,
  sigunguName,
}: {
  regionCares: Array<any>;
  sidoName: string;
  sigunguName?: string;
}) {
  const [tabIndex, setTabIndex] = useState(0);
  const [visibleItems, setVisibleItems] = useState(BATCH_SIZE);

  const lastElementRef = useRef(null);

  useIntersectionObserver({
    root: null,
    target: lastElementRef,
    onIntersect: () => {
      setVisibleItems((prev) =>
        Math.min(prev + BATCH_SIZE, regionCares.length)
      );
    },
  });

  const router = useRouter();

  useEffect(() => {
    const index = TAB_NAME.findIndex((name) => name === location.hash.slice(1));

    if (index >= 0) {
      setTabIndex(index);
    } else {
      setTabIndex(0);
    }
  }, []);
  return (
    <VStack padding="20px 0 0">
      <Tabs
        isFitted
        index={tabIndex}
        onChange={(index) => setTabIndex(index)}
        style={{ color: `${colors.gray500}`, borderColor: `${colors.gray300}` }}
      >
        <TabList>
          <Tab
            style={{
              fontSize: "14px",
            }}
            onClick={() => {
              customEvent({
                newGtm: { path: "care", trigger_and_target: "tab_all" },
              });
              router.replace("#list");
            }}
            _selected={{
              color: `${colors.black}`,
              borderColor: `${colors.black}`,
              fontWeight: 700,
            }}
          >
            {sigunguName ? sigunguName : sidoName} 요양시설 전체 목록
          </Tab>
          <Tab
            style={{
              fontSize: "14px",
            }}
            onClick={() => {
              customEvent({
                newGtm: { path: "care", trigger_and_target: "tab_checklist" },
              });
              router.replace("#check");
            }}
            _selected={{
              color: `${colors.black}`,
              borderColor: `${colors.black}`,
              fontWeight: 700,
            }}
          >
            요양시설 체크리스트
          </Tab>
        </TabList>
        <div style={{ marginTop: "16px" }} />
        <TabPanels>
          <TabPanel padding="20px">
            {tabIndex === 0 && (
              <VStack gap="20px">
                {regionCares.slice(0, visibleItems).map((res, index) => (
                  <CareRegionCard key={res.companyname} {...res} />
                ))}
                <div ref={lastElementRef}></div>
              </VStack>
            )}
          </TabPanel>
          <TabPanel padding="0 10px">
            {tabIndex === 1 && (
              <VStack
                gap="10px"
                className="gray700"
                position="relative"
                width="100%"
                padding="20px"
              >
                <h3 className="subtitle_18_sb gray900">
                  요양병원, 어디이며 누가 가야하나..?
                </h3>
                <p>
                  요양병원은 의사 소견이 있는 누구나 입원할 수 있어요.
                  국민건강보험을 통해 80%의 병원비를 지원받을 수 있는 요양병원은
                  요양원과 달리 상당한 간병비가 별도로 발생할 수 있죠.
                  요양병원별 전문 인력 수준과 일상생활 및 관리 수준으로 의료
                  서비스의 평가 결과가 등급으로 나타나며, 가장 우수한 1등급부터
                  5등급까지 분류돼 있어 사전 체크가 필요해요!
                </p>
                <p>
                  요양병원에는 중풍, 치매, 암 등 노인성 질환으로 재활과 간병
                  서비스, 장기 요양이 필요한 경우. 외과 수술 후 회복을 위해,
                  혹은 노인성 질환을 앓고 있어 단기적인 입원 요양이 필요한 반면
                  보호자 부양이 어려운 경우 입소를 고민할 수 있어요.
                </p>
                <h3 className="subtitle_18_sb gray900">
                  딱 3분 시간 내어, 아래 7개를 체크해 보세요!
                </h3>
                <p css={{ paddingBottom: "140px" }}>
                  좋은 요양 병원을 위한 체크 포인트, 고이가 7개로 정리해
                  보았어요! 아래의 내용들만 잘 숙지...
                </p>
                <VStack
                  position="absolute"
                  css={{
                    bottom: 0,
                    left: 0,
                    background:
                      "linear-gradient(180deg, rgba(255, 255, 255, 0.80) 0%, #FFF 100%)",
                    padding: "20px 0",
                    width: "100%",
                    height: "140px",
                  }}
                  margin="0 0 40px"
                  alignItems="center"
                  gap="8px"
                >
                  <Box className="subtitle_21_b">
                    체크리스트가 필요하신가요?
                  </Box>
                  <Box textAlign="center">
                    아래 버튼을 누르시면 <br />
                    요양시설 체크리스트 전체를 확인하실 수 있어요.
                  </Box>
                  <NextLink
                    href="https://prime.goifuneral.co.kr/content/feeds/care-center/?utm_campaign=care_region_cta"
                    css={{ marginTop: "20px" }}
                    event={{
                      newGtm: { path: "care", trigger_and_target: "CTA" },
                    }}
                  >
                    <HStack
                      gap={10}
                      background={colors.gray900}
                      padding="16px 30px"
                      justifyContent="center"
                      borderRadius="6px"
                    >
                      <span className="button_14_sb white">
                        요양시설 체크리스트 확인하기
                      </span>
                      <ColorIcon
                        name="icon-arrow-right-circle-mono"
                        color={colors.white}
                        size={16}
                      />
                    </HStack>
                  </NextLink>
                </VStack>
              </VStack>
            )}
            <Box css={{ marginBottom: "100px" }}></Box>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </VStack>
  );
}
